import { euclideanModulo } from '@goodboydigital/odie';
import { Container, InteractionEvent, Sprite, Texture, Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../App';
import Curve from '../utils/Curve';
import { Maths } from '../utils/Maths';

const defaultOpts = {
    /** Scroller visible width */
    width: 200,
    /** Scroller visible height */
    height: 300,
    /** Space between items */
    spacing: 250,
    /** Start index for the scroller */
    index: 0,
    /** Offset y position for items */
    offsetY: 0,
    /** Amount of items available */
    length: 4,
};

export type SwipeScrollerContentProvider = (index: number) => Container;
export type SwipeScrollerOptions = typeof defaultOpts;

class SwipeScrollerItem
{
    public index = -1;
    public view = new Container();
}

export class SwipeScroller
{
    public view: Container;
    public content: Container;
    public provider: SwipeScrollerContentProvider;
    public onSnap: Signal<(scroller: SwipeScroller) => void>;
    public onStartDrag: Signal<(scroller: SwipeScroller,) => void>;
    public onStopDrag: Signal<(scroller: SwipeScroller,) => void>;
    public items: SwipeScrollerItem[];

    private area: Sprite;
    private options: SwipeScrollerOptions;
    private pressing = false;
    private dragging = false;
    private position = 0;
    private pointerY = 0;
    private pointerVelY = 0;

    private snapStart = 0;
    private snapEnd = 0;
    private snapDuration = 0.4;
    private snapTime = 0;

    constructor(opts: Partial<SwipeScrollerOptions> = {}, provider: SwipeScrollerContentProvider)
    {
        this.view = new Container();
        this.content = new Container();
        this.view.addChild(this.content);

        this.provider = provider;
        this.options = { ...defaultOpts, ...opts };

        this.onSnap = new Signal();
        this.onStartDrag = new Signal();
        this.onStopDrag = new Signal();

        this.items = [
            this.getItem(),
            this.getItem(),
            this.getItem(),
            this.getItem(),
        ];

        this.area = new Sprite(Texture.WHITE);
        this.area.anchor.set(0.5);
        this.area.alpha = 0.00001;
        this.area.width = this.options.width;
        this.area.height = this.options.height;
        this.area.interactive = true;
        this.area.on('pointerdown', this.onPointerDown.bind(this));
        this.area.on('pointermove', this.onPointerMove.bind(this));
        this.area.on('pointerup', this.onPointerUp.bind(this));
        this.area.on('pointerupoutside', this.onPointerUp.bind(this));
        this.area.on('pointercancel', this.onPointerUp.bind(this));
        this.view.addChild(this.area);

        const mask = new Sprite(Shared.assets.getTexture('gradient-alpha'));

        mask.anchor.set(0.5);
        mask.width = this.options.width;
        mask.height = this.options.height;
        this.view.addChild(mask);
        this.content.mask = mask;

        this.setCurrentIndex(this.options.index);
        Ticker.system.add(this.update, this);
        this.update();
    }

    private onPointerDown(e: InteractionEvent): void
    {
        this.pressing = true;
        this.dragging = false;
        this.pointerY = e.data.global.y;
        this.pointerVelY = 0;
    }

    private onPointerMove(e: InteractionEvent): void
    {
        if (!this.pressing) return;
        this.pointerVelY = e.data.global.y - this.pointerY;
        this.pointerY = e.data.global.y;
        this.position += this.pointerVelY * 0.005;
        if (!this.dragging)
        {
            this.dragging = true;
            this.onStartDrag.emit(this);
        }
    }

    private onPointerUp(): void
    {
        if (!this.pressing) return;
        this.pressing = false;
        this.snapStart = this.position;
        this.snapEnd = Math.round(this.position + (this.pointerVelY * 0.1));
        this.snapDuration = Maths.clamp(Math.abs(this.snapEnd - this.snapStart) * 0.1, 0.3, 1.3);
        this.snapTime = 0;
        if (this.dragging)
        {
            this.dragging = false;
            this.onStopDrag.emit(this);
            Shared.game.soundSystem.playSfx('slide');
        }
    }

    private getItem(): SwipeScrollerItem
    {
        const item = new SwipeScrollerItem();

        this.content.addChild(item.view);

        return item;
    }

    public update(delta = 1): void
    {
        if (!this.pressing && this.snapDuration && this.snapTime < this.snapDuration)
        {
            this.snapTime += delta * (1 / 60);
            let snapped = false;

            if (this.snapTime > this.snapDuration)
            {
                this.snapTime = this.snapDuration;
                snapped = true;
            }
            this.position = Maths.tween(this.snapStart, this.snapEnd, Curve.backOut(this.snapTime / this.snapDuration));

            if (snapped) this.onSnap.emit(this);
        }

        for (let i = 0; i < this.items.length; i++)
        {
            const item = this.items[i];
            const offset = euclideanModulo(this.position, 1) - 1;
            const index = euclideanModulo((Math.ceil(-this.position) + i - 2), this.options.length);

            item.view.y = (this.options.spacing * (i + offset)) - this.options.spacing + this.options.offsetY;
            if (index !== item.index)
            {
                item.index = index;
                item.view.removeChildren();
                item.view.addChild(this.provider(index));
            }
        }
    }

    public refreshContent(): void
    {
        for (const item of this.items) item.index = -1;
    }

    public getCurrentIndex(): number
    {
        return Math.floor(euclideanModulo(-this.position, this.options.length));
    }

    public setCurrentIndex(index: number): void
    {
        this.position = Math.floor(euclideanModulo(-index, this.options.length));
        this.snapEnd = this.position;
        this.snapStart = this.position;
        this.snapEnd = this.position;
    }

    public nextItem(): void
    {
        this.snapStart = this.position;
        this.snapEnd = Math.round(this.position + 1);
        this.snapTime = 0;
        this.snapDuration = 0.3;
    }

    public prevItem(): void
    {
        this.snapStart = this.position;
        this.snapEnd = Math.round(this.position - 1);
        this.snapTime = 0;
        this.snapDuration = 0.3;
    }
}
